import "dayjs/locale/ja";
import { Button, Group } from "@mantine/core";
import { DatePickerInput, DatesRangeValue } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { IconCalendar } from "@tabler/icons-react";
import { lastDayOfMonth, startOfMonth, subMonths } from "date-fns";
import { useTranslation } from "react-i18next";
import { useExecuteTagAssignment } from "@/hooks/useExecuteTagAssignment";

type FormInputs = {
  dateRange: DatesRangeValue;
};

export const ExecuteTagAssignmentButton: React.FC = () => {
  const { t } = useTranslation("admin");
  const { executeTagAssignment, isLoading } = useExecuteTagAssignment();
  const startOfThisMonth = startOfMonth(new Date());
  const defaultPeriodStart = subMonths(startOfThisMonth, 1);
  const defaultPeriodEnd = lastDayOfMonth(defaultPeriodStart);

  const form = useForm<FormInputs>({
    initialValues: {
      dateRange: [defaultPeriodStart, defaultPeriodEnd],
    },
    validateInputOnChange: true,
    validate: {
      dateRange: (value) => !value[0] || !value[1] ? t("tagAssignment.validation.dateRangeRequired") : null,
    },
  });

  async function handleExecuteTagAssignment() {
    const result = form.validate();
    if (result.hasErrors) return;

    const values = form.getValues();
    if (values.dateRange[0] === null || values.dateRange[1] === null) {
      form.setFieldError("dateRange", t("tagAssignment.validation.dateRangeRequired"));
      return;
    }
    await executeTagAssignment(values.dateRange[0], values.dateRange[1]);
  }

  return (
    <form>
      <Group mt={3} align="start">
        <DatePickerInput
          data-testid="dateRange"
          placeholder={t("tagAssignment.selectDateRange")}
          label={t("tagAssignment.targetPeriod")}
          type="range"
          allowSingleDateInRange={true}
          valueFormat="YYYY-MM-DD"
          leftSection={<IconCalendar size={18} stroke={1.5} />}
          leftSectionPointerEvents="none"
          value={form.values.dateRange}
          error={form.errors.dateRange && t("tagAssignment.validation.dateRangeRequired")}
          required
          maxDate={new Date()}
          locale="ja"
          {...form.getInputProps("dateRange")}
          style={{ width: "15rem" }}
        />
        <Button
          data-testid="executeTagAssignmentButton"
          disabled={isLoading}
          onClick={handleExecuteTagAssignment}
          mt="25px"
        >
          {t("tagAssignment.executeButton")}
        </Button>
      </Group>
    </form>
  );
};
